import { useEffect, useState } from "react";
import { NavLink, Navigate, useLocation, Link } from "react-router-dom";
import { Bell, MessageSquare, Home, User, Settings, WifiOff, RefreshCw, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { useAuth, setSalarieOfflineMode } from "../../contexts/AuthContext";
import { useOfflineStatus, markRejectedSeen } from "../../lib/offline/offlineApi";
import { toast } from "sonner";

/** Badge d'icône (PWA installée) : notifications + messages non lus. */
export function applyAppBadge(total) {
  if (typeof navigator === "undefined" || typeof navigator.setAppBadge !== "function") return;
  (total > 0 ? navigator.setAppBadge(total) : navigator.clearAppBadge()).catch(() => {});
}

const TABS = [
  { to: "/salarie", icon: Home, label: "Accueil", end: true, testid: "salarie-tab-home" },
  { to: "/salarie/notifications", icon: Bell, label: "Notifs", testid: "salarie-tab-notifs", badge: "notifs" },
  { to: "/salarie/messages", icon: MessageSquare, label: "Messages", testid: "salarie-tab-messages", badge: "messages" },
  { to: "/salarie/mon-espace", icon: User, label: "Mon espace", testid: "salarie-tab-espace" },
  { to: "/salarie/reglages", icon: Settings, label: "Réglages", testid: "salarie-tab-settings" },
];

/** Coque de l'app « Blitzz Salarié » : manifest dédié, mode hors ligne, barre d'onglets. */
export default function SalarieLayout({ children }) {
  setSalarieOfflineMode(true);
  const { user, loading, axiosAuth } = useAuth();
  const location = useLocation();
  const status = useOfflineStatus();
  const [counts, setCounts] = useState({ notifs: 0, messages: 0 });

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prevHref = link?.getAttribute("href");
    link?.setAttribute("href", "/manifest-salarie.json");
    const title = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const prevTitle = title?.getAttribute("content");
    title?.setAttribute("content", "Blitzz Salarié");
    const theme = document.querySelector('meta[name="theme-color"]');
    const prevTheme = theme?.getAttribute("content");
    theme?.setAttribute("content", "#0b0811");
    const prevDocTitle = document.title;
    document.title = "Blitzz Salarié";
    return () => {
      setSalarieOfflineMode(false);
      if (prevHref) link?.setAttribute("href", prevHref);
      if (prevTitle) title?.setAttribute("content", prevTitle);
      if (prevTheme) theme?.setAttribute("content", prevTheme);
      document.title = prevDocTitle;
    };
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const api = axiosAuth();
    const load = () => api.get("/notifications/counts").then((r) => {
      const notifs = r.data.total || 0; const messages = r.data.messages || 0;
      setCounts({ notifs, messages });
      applyAppBadge(notifs + messages);
    }).catch(() => {});
    load();
    const id = setInterval(load, 20000);
    const onSw = (ev) => { if (ev.data?.type === "PUSH_RECEIVED") load(); };
    window.addEventListener("blitzz:notifs-updated", load);
    navigator.serviceWorker?.addEventListener?.("message", onSw);
    return () => { clearInterval(id); window.removeEventListener("blitzz:notifs-updated", load); navigator.serviceWorker?.removeEventListener?.("message", onSw); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, location.pathname]);

  // Actions hors ligne refusées par le serveur (ex. pointage > 48 h) : on prévient le salarié
  useEffect(() => {
    const unseen = (status.rejected || []).filter((r) => !r.seen);
    if (!unseen.length) return;
    unseen.forEach((r) => toast.error(`${r.label} refusé(e) : ${r.detail}`, { duration: 8000, id: `rej-${r.id}` }));
    markRejectedSeen();
  }, [status.rejected]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0b0811]"><Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" /></div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.must_change_password) return <Navigate to="/dashboard" replace />;

  const isChat = location.pathname.startsWith("/salarie/messages");
  return (
    <div className="h-[100dvh] flex flex-col bg-[#0b0811] text-white" data-testid="salarie-layout">
      {(!status.online || status.pending > 0 || status.syncing) && (
        <div className={`flex items-center justify-between gap-2 px-3 py-1.5 text-[11px] font-semibold ${status.online ? "bg-[#f7c935]/15 text-[#f7c935]" : "bg-amber-500/15 text-amber-300"}`} data-testid="salarie-sync-banner">
          <span className="flex items-center gap-1.5">
            {status.syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : status.online ? <RefreshCw className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            {status.syncing ? "Synchronisation…" : !status.online ? `Hors ligne${status.pending ? ` — ${status.pending} action(s) en attente` : " — lecture des données en cache"}` : `${status.pending} action(s) à synchroniser`}
            {status.lastError && <span className="flex items-center gap-1 text-red-300"><AlertTriangle className="w-3 h-3" />{status.lastError}</span>}
          </span>
          {status.online && status.pending > 0 && !status.syncing && (
            <button onClick={status.sync} className="underline" data-testid="salarie-sync-now">Synchroniser</button>
          )}
        </div>
      )}
      <main className={`flex-1 min-h-0 ${isChat ? "overflow-hidden flex flex-col" : "overflow-y-auto"}`} data-testid="salarie-main">
        {children}
      </main>
      <nav className="flex-shrink-0 border-t border-white/10 bg-[#14101c]/95 backdrop-blur-lg pb-[env(safe-area-inset-bottom)]" data-testid="salarie-tabbar">
        <div className="max-w-3xl mx-auto grid grid-cols-5">
          {TABS.map((t) => (
            <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.testid}
              className={({ isActive }) => `relative flex flex-col items-center gap-0.5 py-2 text-[10px] font-semibold transition-colors ${isActive ? "text-[#f7c935]" : "text-slate-400 hover:text-white"}`}>
              <t.icon className="w-5 h-5" />
              {t.label}
              {t.badge && counts[t.badge] > 0 && <span className="absolute top-1 right-[calc(50%-18px)] min-w-[16px] h-4 px-1 rounded-full bg-[#f97316] text-[#0b0811] text-[9px] font-black flex items-center justify-center" data-testid={`${t.testid}-badge`}>{counts[t.badge] > 99 ? "99+" : counts[t.badge]}</span>}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export function SalarieBackLink({ to = "/salarie", label = "Accueil" }) {
  return <Link to={to} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1" data-testid="salarie-back-link">← {label}</Link>;
}

export function SalarieSyncedBadge({ pending }) {
  return pending ? <span className="text-[10px] text-amber-300 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> En attente</span> : <span className="text-[10px] text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Synchronisé</span>;
}
